/* Missing generic names in USA SubPieces — double-click a row to type the generic;
 * saves to Product Aliases and patches HS DESCRIPTION in the file. */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Check, CheckCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, getAuthHeader } from "@/App";
import { undoLearn } from "@/components/LearnedGenerics";

/* Type-ahead input: pre-filled with the most likely generic for the brand (fuzzy alias match);
 * other alias candidates on top, then Product Aliases generics after 2+ letters. */
const GenericInput = ({ brand, pre, val, setVal, onSave, onCancel }) => {
  const [srv, setSrv] = useState([]);
  const [cands, setCands] = useState(pre?.candidates || []);
  const [touched, setTouched] = useState(false);
  const [hi, setHi] = useState(-1);
  const ref = useRef(null);
  useEffect(() => {
    if (pre?.best) { setVal(pre.best.generic); setTimeout(() => ref.current?.select(), 0); }
  }, [pre, setVal]);
  useEffect(() => {
    const q = val.trim();
    if (!touched || q.length < 2) { setSrv([]); return undefined; }
    const t = setTimeout(() => axios.get(`${API}/product-aliases/generic-suggest`, { params: { q }, headers: getAuthHeader() })
      .then(r => { setSrv(r.data.rows || []); setHi(-1); }).catch(() => setSrv([])), 200);
    return () => clearTimeout(t);
  }, [val, touched]);
  const q = val.trim().toUpperCase();
  const top = cands.map(c => c.generic).filter(g => !touched || g.includes(q));
  const opts = [...new Set([...top, ...srv])].filter(o => o !== q || top.length + srv.length > 1);
  const best = cands.find(c => c.generic === q);
  const rect = opts.length && ref.current ? ref.current.getBoundingClientRect() : null;
  const aliasOf = (g) => cands.find(c => c.generic === g)?.alias_id;
  const pick = (o) => { setVal(o); setSrv([]); setCands([]); onSave(o, aliasOf(o)); };
  const onKey = (e) => {
    if (e.key === "ArrowDown" && opts.length) { e.preventDefault(); setHi(h => (h + 1) % opts.length); }
    else if (e.key === "ArrowUp" && opts.length) { e.preventDefault(); setHi(h => (h <= 0 ? opts.length - 1 : h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); hi >= 0 && opts[hi] ? pick(opts[hi]) : onSave(val, aliasOf(val.trim().toUpperCase())); }
    else if (e.key === "Escape") { opts.length ? (setSrv([]), setCands([])) : onCancel(); }
  };
  return (
    <div className="relative">
      <input ref={ref} autoFocus value={val} onChange={e => { setTouched(true); setVal(e.target.value.toUpperCase()); }} onKeyDown={onKey}
             onBlur={() => (touched && val.trim()) ? onSave(val, aliasOf(val.trim().toUpperCase())) : onCancel()}
             placeholder="Type 2+ letters, e.g. FLU…" className="h-6 w-full px-1.5 border border-amber-400 rounded text-xs uppercase"
             data-testid={`missing-generic-input-${brand}`} />
      {best && <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`missing-generic-hint-${brand}`}>
        from alias: {best.matched} — Enter to confirm</div>}
      {opts.length > 0 && createPortal(
        <ul className="fixed z-[60] max-h-48 overflow-y-auto rounded border border-amber-300 bg-white shadow-lg"
            style={rect ? { left: rect.left, width: rect.width, ...(rect.bottom + 200 > window.innerHeight
              ? { bottom: window.innerHeight - rect.top + 2 } : { top: rect.bottom + 2 }) } : undefined}
            data-testid={`missing-generic-suggest-${brand}`}>
          {opts.map((o, i) => (
            <li key={o} onMouseDown={e => { e.preventDefault(); pick(o); }} onMouseEnter={() => setHi(i)}
                className={`px-2 py-1 text-xs cursor-pointer flex justify-between gap-2 ${i === hi ? "bg-amber-100 text-amber-900" : "text-slate-700"}`}
                data-testid={`missing-generic-option-${i}`}>
              <span>{o}</span>
              {top.includes(o) && <span className="text-[9px] text-emerald-600">alias: {cands.find(c => c.generic === o)?.matched}</span>}
            </li>))}
        </ul>, document.body)}
    </div>
  );
};

export const MissingGenericsPanel = ({ items, entryId, onClose, onSaved }) => {
  const brands = [...new Map((items || []).map(m => [m.brand, m])).values()];
  const [edit, setEdit] = useState(null);
  const [val, setVal] = useState("");
  const [done, setDone] = useState({});
  const saving = useRef(false);
  const [pre, setPre] = useState({});
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const brandKey = brands.map(b => b.brand).join("|");
  useEffect(() => {
    if (!brandKey) return;
    brandKey.split("|").forEach(b => axios.get(`${API}/product-aliases/generic-for-brand`, { params: { brand: b }, headers: getAuthHeader() })
      .then(r => setPre(p => ({ ...p, [b]: r.data }))).catch(() => {}));
  }, [brandKey]);
  if (!brands.length) return null;
  const byBrandDone = (list) => Object.fromEntries(list.map(b => [b.brand, 1]));
  const undoAndRestore = async (ids, brandsBack) => {
    if (!(await undoLearn(ids))) return;
    setDone(d => { const n = { ...d }; brandsBack.forEach(b => delete n[b]); return n; });
    onSaved?.();
  };
  const learnedMsg = (r) => r.learned === "alias" ? ` · learned as alias of ${r.matched}` : r.learned === "created" ? " · new product alias created" : "";
  const prefilled = brands.filter(b => !done[b.brand] && pre[b.brand]?.best);
  const confirmAll = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bulk-data/set-generics`, { entry_id: entryId,
        items: prefilled.map(b => ({ brand: b.brand, generic: pre[b.brand].best.generic, alias_id: pre[b.brand].best.alias_id })) },
        { headers: getAuthHeader() });
      const byBrand = Object.fromEntries(r.data.saved.map(x => [x.brand, x.generic]));
      setDone(d => ({ ...d, ...Object.fromEntries(prefilled.filter(b => byBrand[b.brand.trim().toUpperCase()])
        .map(b => [b.brand, byBrand[b.brand.trim().toUpperCase()]])) }));
      const al = r.data.saved.filter(x => x.learned === "alias").length;
      toast.success(`${r.data.saved.length} generic(s) saved · ${al} learned as alias${r.data.patched_rows ? ` · ${r.data.patched_rows} row(s) fixed in file` : ""}`,
        { duration: 10000, action: { label: "Undo all", onClick: () => undoAndRestore({ batchId: r.data.batch_id }, Object.keys(byBrandDone(prefilled))) } });
      setReview(false); setEdit(null);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const save = async (brand, v = val, aliasId) => {
    if (!v.trim() || saving.current) return;
    saving.current = true;
    try {
      const r = await axios.post(`${API}/bulk-data/set-generic`, { brand, generic: v.trim(), entry_id: entryId, alias_id: aliasId || null },
        { headers: getAuthHeader() });
      setDone(d => ({ ...d, [brand]: r.data.generic }));
      setEdit(null);
      toast.success(`${brand} → ${r.data.generic} saved${learnedMsg(r.data)}${r.data.patched_rows ? ` · ${r.data.patched_rows} row(s) fixed in file` : ""}`,
        { duration: 10000, action: { label: "Undo", onClick: () => undoAndRestore({ logId: r.data.log_id }, [brand]) } });
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { saving.current = false; }
  };
  const left = brands.filter(b => !done[b.brand]).length;
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="bulk-missing-generics">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <h4 className="text-sm font-semibold text-amber-800">Missing generic names ({left})</h4>
        <span className="text-[11px] text-amber-700">HS DESCRIPTION shows "MEDICINE" (yellow) — double-click a row to add the generic once</span>
        {prefilled.length > 0 && (
          <Button size="sm" onClick={() => setReview(true)} data-testid="missing-generic-confirm-all-btn"
                  className="ml-auto h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
            <CheckCheck className="w-3.5 h-3.5 mr-1" /> Confirm all pre-filled ({prefilled.length})
          </Button>)}
        {onClose && <button type="button" onClick={onClose} className={`${prefilled.length ? "" : "ml-auto "} text-amber-700 hover:text-amber-900`} data-testid="bulk-missing-generics-close"><X className="w-4 h-4" /></button>}
      </div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-amber-800"><th className="py-1 pr-2">Brand / product</th><th className="py-1 pr-2">Customer</th><th className="py-1">Generic name</th></tr></thead>
          <tbody>
            {brands.map(m => (
              <tr key={m.brand} className="border-t border-amber-200 cursor-pointer hover:bg-amber-100/60"
                  onDoubleClick={() => { if (!done[m.brand]) { setEdit(m.brand); setVal(""); } }}
                  data-testid={`missing-generic-row-${m.brand}`}>
                <td className="py-1 pr-2 font-semibold text-slate-800">{m.brand}</td>
                <td className="py-1 pr-2 text-slate-600">{m.customer_name}</td>
                <td className="py-1">
                  {done[m.brand] ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Check className="w-3 h-3" />{done[m.brand]}</span>
                    : edit === m.brand ? (
                      <GenericInput brand={m.brand} pre={pre[m.brand]} val={val} setVal={setVal}
                                    onSave={(v, aid) => save(m.brand, v, aid)} onCancel={() => setEdit(null)} />)
                    : pre[m.brand]?.best ? <span className="text-emerald-700" data-testid={`missing-generic-pre-${m.brand}`}>
                        {pre[m.brand].best.generic} <span className="text-[10px] text-slate-500 italic">suggested · double-click to edit</span></span>
                    : <span className="text-amber-600 italic">double-click to add</span>}
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>
      <Dialog open={review} onOpenChange={setReview}>
        <DialogContent className="max-w-lg" data-testid="missing-generic-review">
          <DialogHeader><DialogTitle>Confirm {prefilled.length} pre-filled generic name(s)</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto text-xs">
            <table className="w-full">
              <thead><tr className="text-left text-slate-500"><th className="py-1">Brand</th><th className="py-1">Generic</th><th className="py-1">From alias</th></tr></thead>
              <tbody>{prefilled.map((b, i) => (
                <tr key={b.brand} className="border-t" data-testid={`missing-generic-review-row-${i}`}>
                  <td className="py-1 pr-2 font-semibold">{b.brand}</td>
                  <td className="py-1 pr-2 text-emerald-700 font-semibold">{pre[b.brand].best.generic}</td>
                  <td className="py-1 text-slate-500">{pre[b.brand].best.matched}</td>
                </tr>))}</tbody>
            </table>
            {brands.length - Object.keys(done).length - prefilled.length > 0 && (
              <p className="mt-2 text-amber-700">{brands.length - Object.keys(done).length - prefilled.length} brand(s) without a match stay for manual entry.</p>)}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReview(false)} data-testid="missing-generic-review-cancel">Cancel</Button>
            <Button onClick={confirmAll} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="missing-generic-review-save">
              {busy ? "Saving…" : `Save all ${prefilled.length}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
